import { useState, useEffect } from 'react';
import { C } from '../styles/colours.js';
import { supabase } from '../supabase.js';
import AdminPacks from './AdminPacks.js';

const SERVER_URL = 'https://tabliq-server-production.up.railway.app';

const CATEGORIES = [
  { key: '🍷 Food & Wine', subs: ['🍾 Wine & Drinks', '🍕 World Cuisine', '🧑‍🍳 Cooking & Chefs'] },
  { key: '🎵 Music', subs: ['🎸 Rock & Pop', '🕺 80s & 90s', '🎤 Hip Hop & R&B'] },
  { key: '🌍 Geography', subs: ['🗺️ Capitals & Countries', '🏔️ Nature & Landmarks', '🌎 World Records'] },
  { key: '⚽ Sport', subs: ['⚽ Football', '🎾 Other Sports', '🏅 Olympics'] },
  { key: '🎬 Cinema & TV', subs: ['🎬 Movies', '📺 TV Shows', '🦸 Marvel & DC'] },
  { key: '🧠 General Knowledge', subs: ['🔬 Science', '📚 History', '🎯 Mixed'] },
];

const emptyForm = { q_en: '', a_en: '', q_fr: '', a_fr: '', q_ar: '', a_ar: '', q_es: '', a_es: '' };

function Admin({ onBack }) {
  const [tab, setTab] = useState('questions'); // 'questions' or 'packs'

  // Live stats
  const [live, setLive] = useState(null);

  // Solo Play stats
  const [solo, setSolo] = useState(null);

  // Connection history stats
  const [conn, setConn] = useState(null);

  // Questions tab state
  const [questions, setQuestions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0].key);
  const [selectedSubcategory, setSelectedSubcategory] = useState(CATEGORIES[0].subs[0]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [packCount, setPackCount] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const subs = CATEGORIES.find(c => c.key === selectedCategory)?.subs || [];

  useEffect(() => { if (tab === 'questions') loadQuestions(); }, [selectedCategory, selectedSubcategory, tab]);
  useEffect(() => { loadStats(); loadSoloStats(); loadConnStats(); }, []);

  // Poll live server stats every 5 seconds
  useEffect(() => {
    let active = true;
    const fetchLive = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/stats`);
        const data = await res.json();
        if (active) setLive(data);
      } catch (e) {
        if (active) setLive(null);
      }
    };
    fetchLive();
    const id = setInterval(fetchLive, 5000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('questions')
      .select('*')
      .eq('category', selectedCategory)
      .eq('subcategory', selectedSubcategory)
      .order('created_at', { ascending: false });
    if (error) console.error(error);
    setQuestions(data || []);
    setLoading(false);
  };

  const loadStats = async () => {
    const { count: qCount } = await supabase.from('questions').select('*', { count: 'exact', head: true });
    const { count: pCount } = await supabase.from('quiz_packs').select('*', { count: 'exact', head: true });
    setTotalCount(qCount || 0);
    setPackCount(pCount || 0);
  };

  const loadSoloStats = async () => {
    // New schema: solo_scores has one row per game (user_email, player_name, score, played_at)
    const { data, error } = await supabase
      .from('solo_scores')
      .select('user_email, player_name, score, played_at');
    if (error || !data) return;

    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const todayStr = new Date().toDateString();

    const games = data.length; // one row = one game played
    const players = new Set(data.map(r => r.user_email)).size;
    const playedToday = new Set(
      data.filter(r => r.played_at && new Date(r.played_at).toDateString() === todayStr).map(r => r.user_email)
    ).size;
    const active7 = new Set(
      data.filter(r => r.played_at && (now - new Date(r.played_at).getTime()) < 7 * DAY).map(r => r.user_email)
    ).size;

    // Aggregate per player: total points + 50 bonus per distinct day played (challenge rule)
    const byEmail = {};
    data.forEach(r => {
      const e = r.user_email;
      if (!byEmail[e]) byEmail[e] = { name: r.player_name || e, points: 0, days: new Set() };
      byEmail[e].points += (r.score || 0);
      if (r.player_name) byEmail[e].name = r.player_name;
      if (r.played_at) byEmail[e].days.add(new Date(r.played_at).toDateString());
    });
    const top = Object.values(byEmail)
      .map(v => ({ name: v.name, points: v.points, days: v.days.size, final: v.points + 50 * v.days.size }))
      .sort((a, b) => b.final - a.final)
      .slice(0, 5);

    setSolo({ players, games, playedToday, active7, top });
  };

  const loadConnStats = async () => {
    const { data, error } = await supabase
      .from('daily_stats')
      .select('day, connections')
      .order('day', { ascending: false });
    if (error || !data) return;

    const DAY = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const todayISO = new Date().toISOString().slice(0, 10);

    const total = data.reduce((sum, r) => sum + (r.connections || 0), 0);
    const todayRow = data.find(r => r.day === todayISO);
    const today = todayRow ? todayRow.connections : 0;
    const last7 = data
      .filter(r => (now - new Date(r.day).getTime()) < 7 * DAY)
      .reduce((sum, r) => sum + (r.connections || 0), 0);
    const recentDays = data.slice(0, 7);

    setConn({ today, last7, total, recentDays });
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSave = async () => {
    if (!form.q_en.trim() || !form.a_en.trim()) {
      showMsg('English question and answer are required', 'error');
      return;
    }
    setSaving(true);
    const payload = {
      category: selectedCategory,
      subcategory: selectedSubcategory,
      q_en: form.q_en.trim(),
      a_en: form.a_en.trim(),
      q_fr: form.q_fr.trim() || null,
      a_fr: form.a_fr.trim() || null,
      q_ar: form.q_ar.trim() || null,
      a_ar: form.a_ar.trim() || null,
      q_es: form.q_es.trim() || null,
      a_es: form.a_es.trim() || null,
    };

    let error;
    if (editingId) {
      const r = await supabase.from('questions').update(payload).eq('id', editingId);
      error = r.error;
    } else {
      const r = await supabase.from('questions').insert(payload);
      error = r.error;
    }

    if (error) {
      showMsg('Error: ' + error.message, 'error');
    } else {
      showMsg(editingId ? '✅ Question updated' : '✅ Question added');
      setForm(emptyForm);
      setEditingId(null);
      loadQuestions();
      loadStats();
    }
    setSaving(false);
  };

  const handleEdit = (q) => {
    setForm({
      q_en: q.q_en || '', a_en: q.a_en || '',
      q_fr: q.q_fr || '', a_fr: q.a_fr || '',
      q_ar: q.q_ar || '', a_ar: q.a_ar || '',
      q_es: q.q_es || '', a_es: q.a_es || '',
    });
    setEditingId(q.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question? This cannot be undone.')) return;
    const { error } = await supabase.from('questions').delete().eq('id', id);
    if (error) {
      showMsg('Error: ' + error.message, 'error');
    } else {
      showMsg('🗑️ Question deleted');
      loadQuestions();
      loadStats();
    }
  };

  const handleCancelEdit = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const countLanguages = (q) => {
    let n = 1;
    if (q.q_fr && q.a_fr) n++;
    if (q.q_ar && q.a_ar) n++;
    if (q.q_es && q.a_es) n++;
    return n;
  };

  const inputStyle = {
    width: '100%',
    background: C.bg,
    color: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 2,
    color: C.textMuted,
    marginBottom: 4,
    display: 'block',
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    backgroundSize: 16,
    paddingRight: 34,
  };

  const tabButtonStyle = (isActive) => ({
    flex: 1,
    background: isActive ? C.gold : 'transparent',
    color: isActive ? '#000' : C.textSub,
    border: isActive ? 'none' : `1px solid ${C.border}`,
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
  });

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: 20, maxWidth: 720, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: C.card, border: 'none', color: C.textSub, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>🔒</span>
          <span style={{ fontSize: 11, letterSpacing: 2, color: C.gold, fontWeight: 700 }}>ADMIN ONLY</span>
        </div>
      </div>

      {/* Title */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900, margin: 0, marginBottom: 6 }}>Question bank manager</h1>
        <p style={{ fontSize: 13, color: C.textSub, margin: 0 }}>Add questions, build quiz packs, publish updates live.</p>
      </div>

      {/* LIVE NOW card */}
      <div style={{ background: C.card, border: `1px solid ${C.green}55`, borderRadius: 14, padding: '16px 18px', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11 }}>🟢</span>
          <span style={{ fontSize: 11, letterSpacing: 2, fontWeight: 800, color: C.green }}>LIVE NOW</span>
          <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 'auto' }}>updates every 5s</span>
        </div>

        {live ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 38, fontWeight: 900, lineHeight: 1, color: C.white }}>{live.connections}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>people connected</div>
            </div>
            <div style={{ paddingBottom: 4 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.gold }}>{live.activeRooms}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>games live</div>
            </div>
            <div style={{ paddingBottom: 4 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.gold }}>{live.playersInGames}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>players in games</div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: C.textMuted }}>Connecting to server…</div>
        )}
      </div>

      {/* SOLO PLAY stats card */}
      <div style={{ background: C.card, border: `1px solid ${C.purple}55`, borderRadius: 14, padding: '16px 18px', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11 }}>🎯</span>
          <span style={{ fontSize: 11, letterSpacing: 2, fontWeight: 800, color: C.purple }}>SOLO PLAY</span>
          <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 'auto' }}>signed-in players only</span>
        </div>

        {solo ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: solo.top.length > 0 ? 16 : 0 }}>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: C.white }}>{solo.players}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>players</div>
              </div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: C.purple }}>{solo.games}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>games played</div>
              </div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: C.gold }}>{solo.playedToday}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>played today</div>
              </div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: C.green }}>{solo.active7}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>active 7 days</div>
              </div>
            </div>

            {solo.top.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 8 }}>TOP 5 PLAYERS</div>
                {solo.top.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                    <div style={{ fontSize: 13, width: 22 }}>{['🥇', '🥈', '🥉', '4.', '5.'][i]}</div>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: C.purple, fontWeight: 700 }}>{p.days}d</div>
                    <div style={{ fontSize: 12, color: C.gold, fontWeight: 700, minWidth: 60, textAlign: 'right' }}>{p.final} pts</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13, color: C.textMuted }}>Loading solo stats…</div>
        )}
      </div>

      {/* CONNECTIONS history card */}
      <div style={{ background: C.card, border: `1px solid ${C.blue}55`, borderRadius: 14, padding: '16px 18px', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 11 }}>📡</span>
          <span style={{ fontSize: 11, letterSpacing: 2, fontWeight: 800, color: C.blue }}>CONNECTIONS</span>
          <span style={{ fontSize: 10, color: C.textMuted, marginLeft: 'auto' }}>counting since 11 Jun 2026</span>
        </div>

        {conn ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: conn.recentDays.length > 1 ? 16 : 0 }}>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: C.white }}>{conn.today}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>today</div>
              </div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: C.blue }}>{conn.last7}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>last 7 days</div>
              </div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: C.gold }}>{conn.total}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>all time</div>
              </div>
            </div>

            {conn.recentDays.length > 1 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 8 }}>LAST DAYS</div>
                {conn.recentDays.map((d, i) => (
                  <div key={d.day} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                    <div style={{ flex: 1, fontSize: 12, color: C.textSub }}>{d.day}</div>
                    <div style={{ fontSize: 12, color: C.blue, fontWeight: 700 }}>{d.connections}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13, color: C.textMuted }}>Loading connection stats…</div>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        <div style={{ background: C.card, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Total questions</div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>{totalCount}</div>
        </div>
        <div style={{ background: C.card, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Quiz packs</div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>{packCount}</div>
        </div>
        <div style={{ background: C.card, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Languages</div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>4</div>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button onClick={() => setTab('questions')} style={tabButtonStyle(tab === 'questions')}>📝 Questions</button>
        <button onClick={() => setTab('packs')} style={tabButtonStyle(tab === 'packs')}>📦 Quiz Packs</button>
      </div>

      {/* Message banner (shared across tabs) */}
      {message && (
        <div style={{
          background: message.type === 'error' ? 'rgba(239,71,111,0.15)' : 'rgba(6,214,160,0.15)',
          border: `1px solid ${message.type === 'error' ? C.red : C.green}`,
          borderRadius: 10,
          padding: '10px 14px',
          fontSize: 13,
          marginBottom: 14,
          color: message.type === 'error' ? C.red : C.green,
          fontWeight: 700,
        }}>
          {message.text}
        </div>
      )}

      {/* TAB CONTENT: Quiz Packs */}
      {tab === 'packs' && (
        <AdminPacks onMessage={showMsg} onStatsChange={loadStats} />
      )}

      {/* TAB CONTENT: Questions (existing) */}
      {tab === 'questions' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>CATEGORY</label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategory(CATEGORIES.find(c => c.key === e.target.value).subs[0]);
                }}
                style={selectStyle}
              >
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.key}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>SUBCATEGORY</label>
              <select value={selectedSubcategory} onChange={(e) => setSelectedSubcategory(e.target.value)} style={selectStyle}>
                {subs.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>{editingId ? '✏️' : '➕'}</span>
                <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>{editingId ? 'Edit question' : 'Add new question'}</h3>
              </div>
              {editingId && (
                <button onClick={handleCancelEdit} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.textSub, padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>Cancel edit</button>
              )}
            </div>

            {[
              { lang: 'en', label: 'ENGLISH', dir: 'ltr', required: true },
              { lang: 'fr', label: 'FRANÇAIS', dir: 'ltr', required: false },
              { lang: 'ar', label: 'العربية', dir: 'rtl', required: false },
              { lang: 'es', label: 'ESPAÑOL', dir: 'ltr', required: false },
            ].map(l => (
              <div key={l.lang} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: l.required ? C.gold : C.textMuted, marginBottom: 6 }}>
                  {l.label} {l.required && <span style={{ color: C.red }}>*</span>}
                </div>
                <input
                  type="text"
                  placeholder={`Question (${l.lang.toUpperCase()})`}
                  value={form[`q_${l.lang}`]}
                  onChange={(e) => setForm({ ...form, [`q_${l.lang}`]: e.target.value })}
                  dir={l.dir}
                  style={{ ...inputStyle, marginBottom: 6 }}
                />
                <input
                  type="text"
                  placeholder={`Answer (${l.lang.toUpperCase()})`}
                  value={form[`a_${l.lang}`]}
                  onChange={(e) => setForm({ ...form, [`a_${l.lang}`]: e.target.value })}
                  dir={l.dir}
                  style={inputStyle}
                />
              </div>
            ))}

            <button
              onClick={handleSave}
              disabled={saving || !form.q_en.trim() || !form.a_en.trim()}
              style={{
                width: '100%',
                background: (!saving && form.q_en.trim() && form.a_en.trim()) ? C.green : 'rgba(255,255,255,0.1)',
                color: (!saving && form.q_en.trim() && form.a_en.trim()) ? '#000' : C.textMuted,
                border: 'none',
                borderRadius: 10,
                padding: 12,
                fontSize: 14,
                fontWeight: 800,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              {saving ? '⏳ Saving...' : (editingId ? '💾 Update question' : '✅ Save question')}
            </button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>
              Questions in {selectedSubcategory}
            </h3>
            <span style={{ fontSize: 12, color: C.textSub }}>{questions.length} question{questions.length !== 1 ? 's' : ''}</span>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: 30, color: C.textSub, fontSize: 13 }}>Loading...</div>}

          {!loading && questions.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: C.textMuted, fontSize: 13, background: C.card, borderRadius: 10, border: `1px dashed ${C.border}` }}>
              No questions in this subcategory yet. Add your first one above.
            </div>
          )}

          {!loading && questions.map(q => {
            const langCount = countLanguages(q);
            const allLangs = langCount === 4;
            return (
              <div key={q.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: C.white }}>{q.q_en}</div>
                  <div style={{ fontSize: 11, color: allLangs ? C.green : C.gold, fontWeight: 700 }}>
                    {allLangs ? '✅' : '⚠️'} {q.a_en} · {langCount}/4 languages
                  </div>
                </div>
                <button onClick={() => handleEdit(q)} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: C.white, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }} aria-label="Edit">✏️</button>
                <button onClick={() => handleDelete(q.id)} style={{ background: 'rgba(239,71,111,0.15)', border: 'none', color: C.red, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }} aria-label="Delete">🗑️</button>
              </div>
            );
          })}
        </>
      )}

    </div>
  );
}

export default Admin;